/**
 * Where a district / estate suggestion NAVIGATES — READ from the wire's own
 * hubs, not derived from two numbers and an id.
 *
 * THE DEFECT THIS MAKES UNREPRESENTABLE. The dropdown used to show a mode-blind
 * "1 listing" and link every district/estate to the CURRENT browse mode's hub
 * (`/listings/rent` from the home search). An estate whose only listing was for
 * SALE therefore read "1 listing" and landed on a hub that showed none. The
 * count and the link were computed from two different predicates, and nothing
 * tied them together.
 *
 * LET-89 welded the MODE to the count: the wire started carrying the count PER
 * MODE, computed by the hub's own SQL predicate, and this module became the
 * ONLY way a suggestion turns into a link. That left the SCOPE free. A count
 * said how many and under which mode, and nothing about WHICH LISTINGS — so
 * every client re-derived the listings query from `id` + `type`, and the two
 * mobile clients got it wrong in two different ways (a district id ANDed with
 * the district's NAME as free text; a building id sent as an `estate_id`).
 *
 * `storefront.v1.SuggestionHub` welds the scope too: mode + scope + count, as
 * one value, measured together server-side. This module now READS that value.
 * The web's behaviour does not change — it was already right — but it stops
 * being a second derivation that happens to agree, and becomes a copy of the
 * one the count was measured with. Two properties survive intact:
 *
 *   - a HUB destination needs a `PositiveCount`, which `positiveCount()` — the
 *     one constructor — mints only from a number > 0. So a link to
 *     `/listings/{mode}` cannot be built for a hub whose count is zero: not
 *     "should not", cannot — there is no value of the type to hand it.
 *   - the preferred mode (the surface the user is on) wins when it has a hub;
 *     otherwise the first hub the wire listed; otherwise the entity's own
 *     page, which always exists and never says "0 listings".
 *
 * That choice is the TS twin of `domain_core::search::suggestion_hub_for_mode`
 * (`libs-rust-core/domain-core/src/search.rs`) — `find(mode == preferred)`,
 * else `first()`, else none. See `suggestion-destination.test.ts` for the
 * fixture table that holds the two honest, and for why it is hand-held.
 *
 * The label the row renders is derived from the SAME destination, so it names
 * the mode the link goes to ("1 for sale"), never a total no hub shows — and
 * the href is built from the destination's OWN scope, so the link cannot be
 * filtered to a different entity than the one counted.
 */
import type {
  SuggestionHub,
  SuggestionHubScope,
  SuggestionItemVariants,
} from '@/shared/lib/types/suggestions';
import type { ListingsMode, Route } from '@letsgethome/routes';

declare const positiveCountBrand: unique symbol;

/**
 * A listing count known to be > 0. The brand has exactly one minter,
 * {@link positiveCount}; a hub destination cannot be constructed without one,
 * which is what keeps a zero-count hub link out of the type system rather
 * than out of a code review.
 */
type PositiveCount = number & { readonly [positiveCountBrand]: true };

/** The one minter: `undefined` for anything that is not a positive integer. */
export function positiveCount(n: number): PositiveCount | undefined {
  return Number.isInteger(n) && n > 0 ? (n as PositiveCount) : undefined;
}

/**
 * Where a suggestion goes. `hub` carries the mode it links to, the SCOPE it is
 * filtered to and the count that justified it — all three from one wire hub,
 * so the label, the link and the number it promises are one value read three
 * ways rather than three derivations that must agree.
 */
export type SuggestionDestination =
  | {
      readonly kind: 'hub';
      readonly mode: ListingsMode;
      readonly count: PositiveCount;
      readonly scope: SuggestionHubScope;
      readonly href: Route;
    }
  | { readonly kind: 'entity'; readonly href: Route };

/** The two things a caller must know how to build; the derivation owns the choice. */
type DestinationHrefs = {
  /**
   * The listings hub for the CHOSEN hub's mode and scope.
   *
   * The scope is an ARGUMENT rather than something the builder remembers,
   * because a builder that closes over `item.id` is re-deriving the scope —
   * the exact freedom this change removes. It also means a building row could
   * be handed its estate-scoped hub without the builder having to know that
   * the id it holds is not the id the filter takes.
   */
  readonly hub: (mode: ListingsMode, scope: SuggestionHubScope) => Route;
  /** The entity's own page — the fall-through when there is no navigable hub. */
  readonly entity: Route;
};

/**
 * A suggestion that navigates to a hub.
 *
 * Building suggestions carry hubs too (scoped to their parent estate) and are
 * deliberately NOT here: the row keeps its estate-page link, so its hub is
 * never turned into a destination. That is a RENDERING decision and it is
 * stated where it is rendered (`SuggestionItem.tsx`); this union is the set of
 * variants that actually go through the derivation.
 */
export type HubSuggestion = SuggestionItemVariants['district'] | SuggestionItemVariants['estate'];

/** A chosen hub: the wire's mode and scope, with the count now PROVEN positive. */
type HubChoice = {
  readonly mode: ListingsMode;
  readonly scope: SuggestionHubScope;
  readonly count: PositiveCount;
};

/**
 * Pick the hub a tap opens, preferring the surface the user is already on: the
 * hub for `preferred` if there is one, else the first hub the wire listed,
 * else none. `undefined` is the honest outcome, not a failure — it means the
 * entity has no publicly visible listings in either mode.
 *
 * The TS twin of `domain_core::search::suggestion_hub_for_mode`, with one
 * addition that is local to this trust boundary: a candidate whose count does
 * not mint a `PositiveCount` is skipped rather than returned. The mapper
 * already drops those, so in practice the loop takes its first candidate —
 * but "the wire promised" is not a proof this module is willing to hold a
 * destination on.
 */
export function hubFor(
  hubs: readonly SuggestionHub[],
  preferred: ListingsMode
): HubChoice | undefined {
  const preferredHub = hubs.find((h) => h.mode === preferred);
  const candidates = preferredHub === undefined ? hubs : [preferredHub, ...hubs];
  for (const hub of candidates) {
    const count = positiveCount(hub.count);
    if (count !== undefined) return { mode: hub.mode, scope: hub.scope, count };
  }
  return undefined;
}

/**
 * The single derivation every consumer goes through — the href builders in
 * `SuggestionItem.tsx` and the row's count label alike.
 */
export function suggestionDestination(
  item: HubSuggestion,
  preferred: ListingsMode,
  hrefs: DestinationHrefs
): SuggestionDestination {
  const hub = hubFor(item.hubs, preferred);
  if (hub === undefined) return { kind: 'entity', href: hrefs.entity };
  return {
    kind: 'hub',
    mode: hub.mode,
    count: hub.count,
    scope: hub.scope,
    href: hrefs.hub(hub.mode, hub.scope),
  };
}
